/**
 * Generisches Popover (PV-037, aus BefundPopover herausgelöst). Popover ≠ Modal (CLAUDE.md):
 * Portal an document.body, position:fixed, Nachmessen bei Scroll mit capture:true — neben dem
 * Dokument-Scroll auch innere Scroller (z. B. Phase-Board-Spalten), deren Scroll-Ereignisse nicht
 * zu window aufsteigen (#68) — und bei resize.
 *
 * Schließen: Escape, Klick daneben. Ein Klick auf den Anker schließt hier NICHT — der Anker
 * schaltet selbst um, sonst öffnete ihn derselbe Klick sofort wieder. Solange ein <Modal> offen
 * ist (z. B. ein Dialog aus dem Popover), schließt weder Escape noch ein Hintergrundklick.
 */
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { MODAL_OPEN_ATTRIBUTE } from './modal.js';
import { POPOVER_BREITE, POPOVER_HOEHE, popoverPosition } from './popover-position.js';

export type PopoverProps = {
  anker: RefObject<HTMLElement>;
  onClose: () => void;
  /** Beschriftung der Region (Screenreader, Tests). */
  label: string;
  breite?: number | undefined;
  /** Höchsthöhe; der Inhalt scrollt darüber hinaus. */
  hoehe?: number | undefined;
  children: ReactNode;
};

export function Popover({
  anker,
  onClose,
  label,
  breite = POPOVER_BREITE,
  hoehe = POPOVER_HOEHE,
  children,
}: PopoverProps) {
  const panel = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const messen = () => {
      const el = anker.current;
      if (!el) return;
      setPos(
        popoverPosition(
          el.getBoundingClientRect(),
          window.innerWidth,
          window.innerHeight,
          breite,
          hoehe,
        ),
      );
    };
    messen();
    // capture: true — sieht auch innere Scroller, nicht nur das Dokument (#68).
    window.addEventListener('scroll', messen, true);
    window.addEventListener('resize', messen);
    return () => {
      window.removeEventListener('scroll', messen, true);
      window.removeEventListener('resize', messen);
    };
  }, [anker, breite, hoehe]);

  useEffect(() => {
    const dialogOffen = () => document.body.hasAttribute(MODAL_OPEN_ATTRIBUTE);
    const beiKlick = (e: MouseEvent) => {
      if (dialogOffen()) return;
      const ziel = e.target as Node;
      if (panel.current?.contains(ziel) || anker.current?.contains(ziel)) return;
      onClose();
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dialogOffen()) onClose();
    };
    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiTaste);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiTaste);
    };
  }, [anker, onClose]);

  return createPortal(
    // React-Ereignisse laufen durch Portale zu den React-Vorfahren (Karte, Worklist-Zeile) —
    // deshalb hier stoppen, sonst klappt ein Klick im Popover z. B. die Worklist-Zeile auf.
    // biome-ignore lint/a11y/useKeyWithClickEvents: reiner Propagations-Stopp, keine Aktion
    <section
      ref={panel}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      className="px-3 rounded-lg border"
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: breite,
        maxHeight: hoehe,
        overflowY: 'auto',
        zIndex: 80, // unter <Modal> (z-90), damit Dialoge aus dem Popover darüber liegen
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        boxShadow: 'var(--shadow-lift, 0 12px 32px rgba(0,0,0,0.18))',
      }}
    >
      {children}
    </section>,
    document.body,
  );
}
